import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Domain as DomainIcon,
  Article as ArticleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Layers as TierIcon
} from '@mui/icons-material';
import api, { getPendingDomains } from '../../../../services/api';

const AdminDashboard = () => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    totalArticles: 0,
    totalDomains: 0,
    pendingDomains: 0,
    recentDomains: [],
    totalCPCs: 0,
    averageCPC: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Buscar estatísticas em paralelo
      const [articlesRes, domainsRes, pendingRes, cpcRes] = await Promise.all([
        api.get('/test/articles-count'),
        api.get('/test/domains-count'),
        api.get('/test/pending-domains-count'),
        api.get('/cpc').catch(() => ({ data: { data: [] } })) // Fallback se não conseguir buscar CPC
      ]);

      // Calcular estatísticas de CPC
      const cpcData = cpcRes.data?.data || [];
      const totalCPCs = cpcData.length;
      const averageCPC = totalCPCs > 0
        ? cpcData.reduce((sum, cpc) => sum + cpc.cpc, 0) / totalCPCs
        : 0;

      setStats({
        totalArticles: articlesRes.data.count,
        totalDomains: domainsRes.data.count,
        pendingDomains: pendingRes.data.count,
        recentDomains: [],
        totalCPCs,
        averageCPC
      });
      
      setError('');
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      setError('Erro ao carregar estatísticas: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const StatCard = ({ title, value, icon, color = 'primary', prefix = '' }) => (
    <Card 
      sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: 1, borderColor: 'divider'
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" color="text.primary">
              {prefix}{value.toLocaleString('pt-BR')}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: '50%',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dashboard Administrativo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visão geral do sistema
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Total de Artigos"
            value={stats.totalArticles}
            icon={<ArticleIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Domínios Cadastrados"
            value={stats.totalDomains}
            icon={<DomainIcon sx={{ fontSize: 40, color: 'success.main' }} />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Domínios Pendentes"
            value={stats.pendingDomains}
            icon={<PendingIcon sx={{ fontSize: 40, color: 'warning.main' }} />}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="CPCs Configurados"
            value={stats.totalCPCs}
            icon={<MoneyIcon sx={{ fontSize: 40, color: 'secondary.main' }} />}
            color="secondary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="CPC Médio"
            value={stats.averageCPC.toFixed(2)}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: 'info.main' }} />}
            color="info"
            prefix="R$ "
          />
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card 
            sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: 1, borderColor: 'divider'
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Domínios Recentes Pendentes
              </Typography>
              
              {stats.recentDomains.length === 0 ? (
                <Typography color="textSecondary" textAlign="center" sx={{ py: 4 }}>
                  Nenhum domínio pendente encontrado
                </Typography>
              ) : (
                <List>
                  {stats.recentDomains.map((domain, index) => (
                    <React.Fragment key={domain._id}>
                      <ListItem>
                        <ListItemIcon>
                          <DomainIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={domain.domain}
                          secondary={`${domain.articleCount} artigos • ${new Date(domain.firstSeen).toLocaleDateString('pt-BR')}`}
                        />
                        <Chip
                          label={domain.priority === 1 ? 'Alta' : domain.priority === 2 ? 'Média' : 'Baixa'}
                          size="small"
                          color={domain.priority === 1 ? 'error' : domain.priority === 2 ? 'warning' : 'info'}
                        />
                      </ListItem>
                      {index < stats.recentDomains.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: 1, borderColor: 'divider'
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Ações Rápidas
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<PendingIcon />}
                  onClick={() => window.location.href = '/admin/pending-domains'}
                  fullWidth
                >
                  Gerenciar Domínios Pendentes
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<TierIcon />}
                  onClick={() => window.location.href = '/admin/tiers'}
                  fullWidth
                >
                  Gerenciar Escalas de Tier
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => window.location.href = '/admin/analytics'}
                  fullWidth
                >
                  Ver Analytics
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => window.location.href = '/admin/users'}
                  fullWidth
                >
                  Gerenciar Usuários
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
